import { useDispatch } from 'react-redux'
import type { AppDispatch } from '@/store'

// Import slice actions
import { globalFiltersActions } from '@/store/globalFiltersSlice'

// Map slice names to their actions
const actionsMap = {
  globalFilters: globalFiltersActions,
} as const

// Extract only function keys from a slice
type FunctionKeys<T> = {
  [K in keyof T]: T[K] extends (...args: any[]) => any ? K : never
}[keyof T]

// Extract slice actions type
type ActionCreatorsOf<Slice extends keyof typeof actionsMap> =
  (typeof actionsMap)[Slice]

// Return type: all dispatch-wrapped actions

type WrappedActions<Slice extends keyof typeof actionsMap> = {
  [K in FunctionKeys<
    ActionCreatorsOf<Slice>
  >]: ActionCreatorsOf<Slice>[K] extends (...args: infer P) => any
    ? (...args: P) => ReturnType<AppDispatch>
    : never
}

export function useSliceDispatch<Slice extends keyof typeof actionsMap>(
  slice: Slice
): WrappedActions<Slice> {
  const dispatch = useDispatch<AppDispatch>()
  const sliceActions = actionsMap[slice]

  const wrapped = {} as WrappedActions<Slice>

  // Object.keys returns string[], so we cast to proper keys
  ;(
    Object.keys(sliceActions) as Array<FunctionKeys<typeof sliceActions>>
  ).forEach((key) => {
    const actionCreator = sliceActions[key]

    if (typeof actionCreator === 'function') {
      wrapped[key] = ((...args: any[]) =>
        dispatch(actionCreator(...args))) as any
    }
  })

  return wrapped
}
